import { z } from 'zod';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * パスワードバリデーション（superRefine）
 *
 * パスワードは任意入力（空の場合はサーバー側で更新しない）。
 * パスワードを入力した場合のみ 8文字以上・英数字混合・確認一致を検証する。
 */
const passwordRefinement = (
  data: { password?: string; passwordConfirmation?: string },
  ctx: z.RefinementCtx
): void => {
  const { password, passwordConfirmation } = data;
  if (!password) return;

  if (password.length < 8) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードは8文字以上入力してください。',
      path: ['password'],
    });
  } else if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードは英数字混合にしてください。',
      path: ['password'],
    });
  }

  if (password !== passwordConfirmation) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードとパスワード（再入力）が一致しません。',
      path: ['passwordConfirmation'],
    });
  }
};

/**
 * メールアドレス・電話番号の「どちらか必須」バリデーション（superRefine）
 *
 * requireEitherEmailOrPhoneNumber が true の場合のみ適用する
 * （activation_code 有効 × login_method='phone_number' × is_activated=true のケース。
 * logics/resolveFieldRequirements 参照）。両方空のときのみ email / phoneNumber の
 * 両フィールドにエラーを追加する（片方でも入力があれば valid）。
 *
 * @param requireEitherEmailOrPhoneNumber - 「どちらか必須」ガードを適用するかどうか
 */
const createEitherEmailOrPhoneNumberRefinement =
  (requireEitherEmailOrPhoneNumber: boolean) =>
  (data: { email?: string; phoneNumber?: string }, ctx: z.RefinementCtx): void => {
    if (!requireEitherEmailOrPhoneNumber) return;
    if (data.email || data.phoneNumber) return;

    const message = 'メールアドレスまたは電話番号のいずれかを入力してください。';
    ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['email'] });
    ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['phoneNumber'] });
  };

/**
 * メールアドレスのバリデーション
 *
 * isEmailRequired が true の場合は必須（空文字不可・形式チェック）、
 * false の場合は任意（空文字許容、入力がある場合のみ形式チェック）とする。
 * 必須/任意の判定は logics/resolveFieldRequirements の結果を利用する。
 *
 * @param isEmailRequired - メールアドレスが必須かどうか
 */
const createEmailSchema = (isEmailRequired: boolean) =>
  isEmailRequired
    ? z
        .string()
        .min(1, 'メールアドレスが入力されていません。')
        .regex(EMAIL_REGEX, 'メールアドレスの形式が正しくありません。')
    : z
        .string()
        .refine(
          value => value === '' || EMAIL_REGEX.test(value),
          'メールアドレスの形式が正しくありません。'
        );

/**
 * 利用者編集フォームの Zod スキーマを生成する
 *
 * メールアドレスの必須/任意は isEmailRequired（logics/resolveFieldRequirements の判定結果）で切り替える。
 * requireEitherEmailOrPhoneNumber が true の場合、メールアドレス・電話番号のどちらか一方の
 * 入力を必須とする cross-field バリデーションを追加する（両方空を不可にする）。
 * パスワードは任意（入力した場合のみ検証）。
 * billableFrom は通常ユーザーは必須。家族招待ユーザー（is_invitee === true）の場合は任意（PEN-968）。
 *
 * @param isEmailRequired - メールアドレスが必須かどうか
 * @param requireEitherEmailOrPhoneNumber - メールアドレス・電話番号のどちらか一方の入力を必須とするか
 * @param isInvitee - 家族招待ユーザーかどうか（convertToPresentationProps で導出）
 */
export const createEditUserFormSchema = (
  isEmailRequired: boolean,
  requireEitherEmailOrPhoneNumber: boolean,
  isInvitee: boolean
) =>
  z
    .object({
      fullName: z.string().min(1, 'フルネームが入力されていません。'),
      email: createEmailSchema(isEmailRequired),
      // 電話番号フィールドの表示可否（isPhoneNumberFieldVisible）は Presentation 側の関心事であり、
      // バリデーションルールは表示有無に関わらず設けない（任意項目）。
      phoneNumber: z.string().optional(),
      externalUserId: z.string().optional(),
      password: z.string().optional(),
      passwordConfirmation: z.string().optional(),
      // 【PEN-968 対応】家族招待ユーザー（is_invitee === true）は非課金対象のため、
      // billableFrom は null のまま保存可能。通常ユーザーは必須バリデーション（refine + transform）を適用する。
      billableFrom: isInvitee
        ? z.date({ invalid_type_error: '利用開始日を選択してください。' }).nullable()
        : z
            .date({ invalid_type_error: '利用開始日を選択してください。' })
            .nullable()
            .refine(v => v !== null, { message: '利用開始日を選択してください。' })
            .transform(v => v as Date),
    })
    .superRefine(passwordRefinement)
    .superRefine(createEitherEmailOrPhoneNumberRefinement(requireEitherEmailOrPhoneNumber));

export type EditUserFormFieldValues = z.infer<ReturnType<typeof createEditUserFormSchema>>;
